/**
 * Shared formatter utilities for dates, names, and other display values.
 * Consolidates duplicate formatting logic from across the application.
 */

/**
 * Format a timestamp as a relative time string (e.g., "2 days ago", "Just now").
 * Used for displaying recent activity in lists and cards.
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Relative time string
 *
 * @example
 * formatRelativeDate(Date.now() - 1000 * 60 * 5) // "Just now"
 * formatRelativeDate(Date.now() - 1000 * 60 * 60 * 24 * 3) // "3 days ago"
 */
export function formatRelativeDate(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return "Today";
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  if (diffDays < 7) {
    return `${diffDays} days ago`;
  }
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} ${months === 1 ? "month" : "months"} ago`;
  }
  const years = Math.floor(diffDays / 365);
  return `${years} ${years === 1 ? "year" : "years"} ago`;
}

/**
 * Format a timestamp for conversation timestamps (Teams panel).
 * Shows time for today, weekday for recent, or abbreviated date.
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Formatted date string
 *
 * @example
 * // Today
 * formatConversationDate(Date.now()) // "3:45 PM"
 *
 * // Yesterday
 * formatConversationDate(Date.now() - 86400000) // "Yesterday"
 *
 * // This week
 * formatConversationDate(Date.now() - 172800000) // "Mon"
 *
 * // Older
 * formatConversationDate(Date.now() - 864000000) // "Jan 5"
 */
export function formatConversationDate(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  }
  if (diffDays === 1) {
    return "Yesterday";
  }
  if (diffDays < 7) {
    return date.toLocaleDateString("en-US", { weekday: "short" });
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

/**
 * Format a timestamp as a full date and time string.
 * Used for displaying detailed timestamps in tooltips and detail views.
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Formatted date string (e.g., "Jan 15, 2024, 3:45 PM")
 *
 * @example
 * formatFullDate(1705339500000) // "Jan 15, 2024, 3:45 PM"
 */
export function formatFullDate(timestamp: number): string {
  return new Date(timestamp).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Format a timestamp as a smart relative/absolute date for version history items.
 * Granularity decreases as the date gets older, with an absolute fallback including year.
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @param now - The clock to read the distance against. A surface that owns a clock passes it,
 *   so what it renders is a function of its own state rather than of the moment the string was
 *   built; the default is for the surfaces that have none.
 * @returns Human-readable date string
 *
 * @example
 * formatSmartDate(Date.now() - 30000)               // "just now"
 * formatSmartDate(Date.now() - 1000 * 60 * 5)       // "5m ago"
 * formatSmartDate(Date.now() - 1000 * 60 * 60 * 3)  // "3h ago"
 * formatSmartDate(Date.now() - 1000 * 60 * 60 * 25) // "Yesterday"
 * formatSmartDate(Date.now() - 1000 * 60 * 60 * 24 * 10) // "Feb 13, 2026"
 */
export function formatSmartDate(timestamp: number, now: number = Date.now()): string {
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;

  const date = new Date(timestamp);
  const today = new Date(now);
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format a byte count as a human-readable file size.
 *
 * Returns `null` — not `"0 B"` and not `"Unknown size"` — when the size is missing, zero,
 * or not a number, so each surface picks its own copy for "we don't know how big this is"
 * instead of inheriting one component's wording.
 *
 * @param bytes - Size in bytes
 * @returns Formatted size, or `null` when there is nothing meaningful to show
 *
 * @example
 * formatFileSize(512)      // "512 B"
 * formatFileSize(4300)     // "4.2 KB"
 * formatFileSize(5_242_880) // "5.0 MB"
 * formatFileSize(undefined) // null
 */
export function formatFileSize(bytes: number | null | undefined): string | null {
  if (!bytes || Number.isNaN(bytes)) return null;
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(1)} MB`;
  return `${(mb / 1024).toFixed(1)} GB`;
}

/**
 * How a duration reads. One body, three vocabularies — pick the one the surface needs
 * instead of writing a fourth.
 *
 * - `short` — `"320 ms"` / `"1.4 s"` / `"90.0 s"`. Table cells where the magnitude is the point.
 * - `precise` — adds two decimals below 10 s and a minutes tier: `"4.20 s"` / `"1.5 min"`.
 *   For run/tool telemetry, where the difference between 4.2 s and 4.24 s is the reason to look.
 * - `clock` — `"3m 05s"` elapsed wall time, for a counter that ticks while work runs.
 * - `media` — `"42:18"` / `"1:04:12"`, the timecode every media player shows. For the *length
 *   of a recording*, which is a fact about a file rather than a measurement of some work: a
 *   person who knows their meeting ran an hour recognises `1:04:12` and not `64.2 min`.
 */
export type DurationStyle = "short" | "precise" | "clock" | "media";

/**
 * Format a millisecond duration for display.
 *
 * `absent` is what to render when there is no duration to show; it defaults to an em dash,
 * and surfaces whose other cells say `"n/a"` pass that instead so one table speaks one way.
 *
 * @example
 * formatDuration(320)                              // "320 ms"
 * formatDuration(1420)                             // "1.4 s"
 * formatDuration(4200, { style: "precise" })       // "4.20 s"
 * formatDuration(95_000, { style: "precise" })     // "1.6 min"
 * formatDuration(185_000, { style: "clock" })      // "3m 05s"
 * formatDuration(2_538_000, { style: "media" })    // "42:18"
 * formatDuration(null, { absent: "n/a" })          // "n/a"
 */
export function formatDuration(
  durationMs: number | null | undefined,
  options: { style?: DurationStyle; absent?: string } = {}
): string {
  const { style = "short", absent = "—" } = options;
  if (typeof durationMs !== "number" || !Number.isFinite(durationMs)) return absent;

  if (style === "clock") {
    const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${String(seconds).padStart(2, "0")}s`;
  }

  if (style === "media") {
    const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
    const hours = Math.floor(totalSeconds / 3_600);
    const minutes = Math.floor((totalSeconds % 3_600) / 60);
    const seconds = String(totalSeconds % 60).padStart(2, "0");
    // The hour place appears only when there is one — `0:42:18` is not how anybody reads a
    // forty-minute recording — and the minutes are padded once it does, so a list of lengths
    // stays aligned on the colon.
    return hours > 0
      ? `${hours}:${String(minutes).padStart(2, "0")}:${seconds}`
      : `${minutes}:${seconds}`;
  }

  if (durationMs < 1000) return `${Math.round(durationMs)} ms`;
  if (style === "short" || durationMs < 60_000) {
    return `${(durationMs / 1000).toFixed(style === "precise" && durationMs < 10_000 ? 2 : 1)} s`;
  }
  return `${(durationMs / 60_000).toFixed(1)} min`;
}

/**
 * Abbreviate a large count — token counts, budgets, deltas — to one decimal place.
 *
 * Lowercase `k`, uppercase `M`: the vocabulary the composer's context badge already shows
 * the people who use the product, now shared by the admin tables that used to say `K`.
 * Negative values keep their sign (`"-1.2k"`), so deltas read correctly.
 *
 * @example
 * formatCompactNumber(940)        // "940"
 * formatCompactNumber(12_345)     // "12.3k"
 * formatCompactNumber(1_240_000)  // "1.2M"
 * formatCompactNumber(null)       // "—"
 */
export function formatCompactNumber(
  value: number | null | undefined,
  options: { absent?: string } = {}
): string {
  const { absent = "—" } = options;
  if (typeof value !== "number" || !Number.isFinite(value)) return absent;
  const magnitude = Math.abs(value);
  if (magnitude >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (magnitude >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(Math.round(value));
}

/**
 * Locale codes as the languages a person reads them as — `["pl-PL", "en-US"]` → `"Polish,
 * English"`.
 *
 * The names come from `Intl.DisplayNames` in the *reader's* own language, which is the whole
 * reason this is a browser formatter and the backend stores codes: what `pl-PL` is called depends
 * on who is asking, and a name resolved once on the server would be resolved in the wrong one.
 * Only the language subtag is looked up — a locale names a region as well, and "Polish" is the
 * answer to "what language is this", not "Polish (Poland)".
 *
 * A code the browser has no name for falls back to the code itself, and a duplicate name is kept
 * once: two locales of one language (`en-US`, `en-GB`) are one language to a reader. The order is
 * the caller's, which for a transcript is most-spoken first.
 *
 * @example
 * formatLanguageNames(["pl-PL"])            // "Polish"
 * formatLanguageNames(["pl-PL", "en-US"])   // "Polish, English"
 * formatLanguageNames(["zz"])               // "zz"
 * formatLanguageNames([])                   // null
 */
export function formatLanguageNames(
  locales: readonly string[] | undefined,
  options: { displayLocale?: string } = {}
): string | null {
  if (!locales || locales.length === 0) return null;
  const displayNames = createLanguageDisplayNames(options.displayLocale);
  const names: string[] = [];
  for (const locale of locales) {
    const name = resolveLanguageName(displayNames, locale);
    if (name !== null && !names.includes(name)) names.push(name);
  }
  return names.length > 0 ? names.join(", ") : null;
}

/**
 * The reader's own `Intl.DisplayNames`, or `null` where the runtime has none.
 *
 * Guarded rather than assumed: `Intl.DisplayNames` throws on an invalid display locale, and a
 * name a browser cannot produce must degrade to the code rather than take the row down.
 */
function createLanguageDisplayNames(displayLocale: string | undefined): Intl.DisplayNames | null {
  try {
    // `navigator` is guarded rather than assumed: this module is imported by pure view models
    // that a unit test runs outside a document.
    const reader = typeof navigator === "undefined" ? undefined : navigator.language;
    return new Intl.DisplayNames([displayLocale ?? reader ?? "en"], { type: "language" });
  } catch {
    return null;
  }
}

/** One code's language name, or the code itself where nothing can name it. */
function resolveLanguageName(
  displayNames: Intl.DisplayNames | null,
  locale: string
): string | null {
  const code = locale.trim();
  if (code.length === 0) return null;
  const subtag = code.split(/[-_]/)[0]?.toLowerCase();
  if (!subtag) return null;
  try {
    // `of` returns the code back for a subtag it has no name for, which is exactly the fallback
    // this wants — and `undefined` under `{ fallback: "none" }`, which no caller here asks for.
    return displayNames?.of(subtag) ?? code;
  } catch {
    return code;
  }
}

// Initials live in `@repo/shared` (`getInitials`), because the backend renders them too —
// import them from there rather than adding a display-only copy here.
// Casing and key-humanizing helpers (`humanizeKey`, `titleCaseSegment`, `capitalizeWords`)
// live there too, for the same reason.

/**
 * A count and its noun, agreeing: `1 project`, `3 projects`, `0 projects`.
 *
 * The irregular plural is passed rather than guessed — no rule beyond "add an s" belongs in a
 * formatter that every screen shares.
 *
 * @example
 * pluralize(1, "term")               // "1 term"
 * pluralize(4, "term")               // "4 terms"
 * pluralize(2, "entity", "entities") // "2 entities"
 */
export function pluralize(count: number, singular: string, plural?: string): string {
  const word = count === 1 ? singular : (plural ?? `${singular}s`);
  return `${count} ${word}`;
}
